import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { PricingFields } from "@/components/invoices/PricingFields";
import { PaymentFields } from "@/components/invoices/PaymentFields";

const toForm = (s) => ({
  ...s, hourly_rate: s.hourly_rate ? String(s.hourly_rate) : "", terms_days: String(s.terms_days ?? 14),
  type_prices: Object.fromEntries(Object.entries(s.type_prices || {}).map(([k, v]) => [k, String(v)])),
  items: (s.items || []).map((i) => ({ description: i.description, price: String(i.price) })),
  prefix: s.prefix ?? "INV-", next_number: String(s.next_number ?? 1),
});

const toBody = (f, company) => ({
  pricing: f.pricing, hourly_rate: Number(f.hourly_rate) || 0, terms_days: parseInt(f.terms_days, 10) || 0,
  type_prices: Object.fromEntries(Object.entries(f.type_prices).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v) || 0])),
  items: f.items.filter((i) => i.description.trim()).map((i) => ({ description: i.description.trim(), price: Number(i.price) || 0 })),
  gst: f.gst, bank_name: f.bank_name, bank_bsb: f.bank_bsb, bank_account: f.bank_account, card: f.card,
  notes: f.notes, reminders: f.reminders, reply_to: f.reply_to,
  ...(company ? { prefix: f.prefix, next_number: parseInt(f.next_number, 10) || 1 } : {}),
});

const DetailFields = ({ f, set, company }) => (
  <section className="space-y-4" data-testid="settings-details">
    <h3 className="font-semibold">Invoice details</h3>
    <label className="flex items-center justify-between gap-3 text-sm"><span>GST registered (adds 10% and titles it "Tax invoice")</span>
      <Switch checked={f.gst} onCheckedChange={(v) => set("gst", v)} data-testid="settings-gst-toggle" /></label>
    {company && (
      <div className="grid grid-cols-2 gap-3 max-w-sm">
        <div className="space-y-1.5"><Label className="text-xs">Number prefix</Label><Input value={f.prefix} onChange={(e) => set("prefix", e.target.value)} data-testid="settings-prefix" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Next number</Label><Input value={f.next_number} inputMode="numeric" onChange={(e) => set("next_number", e.target.value)} data-testid="settings-next-number" /></div>
      </div>
    )}
    <div className="space-y-1.5"><Label className="text-xs">Note printed on every invoice</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="settings-notes" /></div>
    <div className="space-y-1.5 max-w-sm"><Label className="text-xs">Client replies go to (blank = whoever sends it)</Label><Input type="email" value={f.reply_to} onChange={(e) => set("reply_to", e.target.value)} data-testid="settings-reply-to" /></div>
    <label className="flex items-center justify-between gap-3 text-sm"><span>Email overdue reminders automatically (day after due, then weekly, up to 3)</span>
      <Switch checked={f.reminders} onCheckedChange={(v) => set("reminders", v)} data-testid="settings-reminders-toggle" /></label>
  </section>
);

export const InvoiceSettingsDialog = ({ open, onClose, onSaved }) => {
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.invoiceSettings().then((x) => { setS(x); setF(toForm(x)); }).catch(() => toast.error("Could not load invoice settings")); }, [open]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const x = await api.saveInvoiceSettings(toBody(f, s.can_manage_company)); setS(x); setF(toForm(x)); toast.success("Invoice settings saved"); onSaved?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save settings"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Use the company-wide invoice settings for this depot?")) return;
    const x = await api.resetInvoiceSettings(); setS(x); setF(toForm(x)); toast.success("Now using company settings");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Invoice settings</DialogTitle>
          <DialogDescription data-testid="invoice-settings-scope">{!s ? "Loading…" : s.scope === "depot"
            ? `For the ${s.depot_name} depot${s.depot_override ? "" : " (currently using the company settings)"}.`
            : "Company-wide. Every depot uses these unless it saves its own."}</DialogDescription>
        </DialogHeader>
        {!f ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="space-y-8">
            <PricingFields f={f} set={set} jobTypes={s.job_types || []} />
            <PaymentFields f={f} set={set} />
            <DetailFields f={f} set={set} company={s.can_manage_company} />
          </div>
        )}
        <DialogFooter className="gap-2">
          {s?.scope === "depot" && s.depot_override && <Button variant="ghost" onClick={reset} className="rounded-full mr-auto" data-testid="settings-reset-depot">Use company settings</Button>}
          <Button disabled={!f || saving} onClick={save} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="settings-save-btn">{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
